import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Layout from "@/components/layout/Layout";
import PageHeader from "@/components/layout/PageHeader";
import Seo from "@/components/Seo";
import { PRICING } from "@/data/siteData";

const PricingPage = () => {
  return (
    <Layout>
      <Seo path="/ceni" />
      <PageHeader 
        subtitle="Ценова политика"
        title="Цени"
        description="Прозрачни и конкурентни цени за нашите услуги"
      />

      {/* Disclaimer */}
      <section className="py-8 bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <p className="text-amber-800 text-center">
              <strong>Важно:</strong> Показаните суми са ориентировъчни и имат
              за цел да дадат ясна представа за диапазона на услугите. Те не са
              крайни и подлежат на допълнително договаряне. Описаните суми са
              без ДДС.
            </p>
          </div>
        </div>
      </section>

      {/* Chat Channels Pricing */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Управление на чат канали
            </h2>
            <p className="text-slate-600 mt-2">
              Месечни абонаментни планове за всеки канал
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {PRICING.map((item, index) => (
              <div
                key={index}
                className="pricing-card"
                data-testid={`pricing-card-${index}`}
              >
                <h3 className="text-xl font-bold text-[#2E4A91] mb-4">
                  {item.channel}
                </h3>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-bold text-slate-900">
                    {item.monthly}
                  </span>
                  <span className="text-slate-500">EUR / месец</span>
                </div>
                <p className="text-slate-600 mb-6">
                  + {item.perTicket} EUR / тикет
                </p>
                <ul className="space-y-3 text-sm text-slate-600">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                    Обработка на съобщения
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                    Филтриране на запитвания
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                    Отчетност и анализ
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                    Изходящи кампании
                  </li>
                </ul>
              </div>
            ))}

            {/* All Together Card */}
            <div
              className="pricing-card featured sm:col-span-2 lg:col-span-1"
              data-testid="pricing-card-all"
            >
              <div className="absolute top-4 right-4 bg-[#2ED573] text-slate-900 text-xs font-bold px-3 py-1 rounded-full">
                ПАКЕТ
              </div>
              <h3 className="text-xl font-bold text-[#2E4A91] mb-4">
                Всички канали
              </h3>
              <div className="flex items-baseline gap-1 mb-2">
                <span className="text-4xl font-bold text-slate-900">550</span>
                <span className="text-slate-500">EUR / месец</span>
              </div>
              <p className="text-slate-600 mb-6">Включва всички 5 канала</p>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                  WhatsApp, Viber, Instagram
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                  Webchat, SMS
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                  Единен център за управление
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                  Приоритетна поддръжка
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Call Center Pricing */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Кол Център услуги
            </h2>
            <p className="text-slate-600 mt-2">
              Индивидуални оферти според вашите нужди
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-sm border border-slate-100">
              <div className="text-center mb-8">
                <div className="text-5xl font-bold text-[#2E4A91] mb-2">
                  Персонализирана оферта
                </div>
                <p className="text-slate-600">
                  Цената зависи от обема и спецификата на кампанията
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-[#2E4A91] mb-4">
                    Входяща линия
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Денонощна поддръжка 24/7
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Фронт офис обслужване
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Техническа поддръжка
                    </li>
                  </ul>
                </div>
                <div className="bg-slate-50 rounded-xl p-6">
                  <h4 className="font-semibold text-[#2E4A91] mb-4">
                    Изходяща линия
                  </h4>
                  <ul className="space-y-2 text-sm text-slate-600">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Търговски кампании
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Проучвания и анкети
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-[#2ED573]" />
                      Проследяване на плащания
                    </li>
                  </ul>
                </div>
              </div>

              <div className="text-center">
                <Link to="/zapazi-chas">
                  <Button className="btn-primary text-lg px-10 py-6">
                    Поискайте оферта
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-white">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">
              Често задавани въпроси
            </h2>
          </div>

          <div className="space-y-6">
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-[#2E4A91] mb-2">
                Какво е включено в месечната такса?
              </h3>
              <p className="text-slate-600">
                Месечната такса включва базовата настройка на канала, достъп до
                платформата за управление и основна отчетност. Таксата за тикет
                се начислява допълнително според обема.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-[#2E4A91] mb-2">
                Има ли минимален договорен период?
              </h3>
              <p className="text-slate-600">
                Предлагаме гъвкави условия. Стандартният договор е за 3 месеца,
                но при по-големи обеми можем да предложим месечни условия.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-[#2E4A91] mb-2">
                Как се определя цената за кол център услуги?
              </h3>
              <p className="text-slate-600">
                Цената зависи от множество фактори: обем на обажданията, работно
                време, сложност на продукта/услугата, нужда от обучение и др.
                Свържете се с нас за персонализирана оферта.
              </p>
            </div>
            <div className="bg-slate-50 rounded-xl p-6">
              <h3 className="font-semibold text-[#2E4A91] mb-2">
                Предлагате ли отстъпки за пакетни услуги?
              </h3>
              <p className="text-slate-600">
                Да, при комбиниране на няколко услуги предлагаме атрактивни
                отстъпки. Пакетът за всички чат канали е пример за такава
                икономия.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-[#2E4A91] to-[#243b75] py-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Нуждаете се от персонализирана оферта?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Свържете се с нас и ще подготвим оферта, съобразена с вашите
            специфични нужди
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/zapazi-chas">
              <Button className="bg-white text-[#2E4A91] hover:bg-slate-100 text-lg px-8 h-14 rounded-lg font-semibold">
                Запазете консултация
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/kontakti">
              <Button
                variant="outline"
                className="border-white text-white hover:bg-white/10 text-lg px-8 h-14 rounded-lg font-semibold"
              >
                Свържете се с нас
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default PricingPage;
